import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { NavLink } from "react-router-dom";
import { ChevronRight, Calendar, MapPin, Clock, Play } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useState, useRef } from "react";

const Index = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const images = [
    { src: "/sam.webp", alt: "Sam Childers" },
    { src: "/images/sam-childers.webp", alt: t("about.photo.bike") },
    {
      src: "/lovable-uploads/56913f09-fb42-4df9-8bcb-80b1e6405d2e.png",
      alt: "Gallery Image 1",
    },
    {
      src: "/lovable-uploads/c5680540-6f46-4041-9b8f-129e5b82289d.png",
      alt: "Gallery Image 2",
    },
    {
      src: "/lovable-uploads/2aeccf39-c2f4-4067-b36f-01c22b30f90a.png",
      alt: "Gallery Image 3",
    },
    {
      src: "/lovable-uploads/a21377fa-8dad-4704-bed7-0540e2a448b9.png",
      alt: "Gallery Image 4",
    },
  ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setIsOpen(true);
  };

  // Set hero background style
  const heroStyle = {
    "--bg-image":
      "url('/lovable-uploads/df481845-4045-41c1-9dd3-80b32c49e6f6.png')",
    backgroundPosition: "center 20%",
  } as React.CSSProperties;

  return (
    <>
      {/* Hero Section */}
      <section className="bg-hero min-h-screen flex items-center py-20 md:py-32" style={heroStyle}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <div className="animate-fade-in text-left space-y-6 md:space-y-8">
              <div className="space-y-2 md:space-y-4">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                  {t("hero.title")}
                </h2>
                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-primary">
                  {t("hero.subtitle")}
                </h1>
              </div>
              <p className="text-lg md:text-xl text-white/90 max-w-xl">
                {t("hero.description")}
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button asChild size="lg" variant="destructive">
                  <NavLink to="/buy-ticket">
                    {t("nav.buyTicket")} <ChevronRight className="ml-2 h-4 w-4" />
                  </NavLink>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <NavLink to="/events">{t("nav.events")}</NavLink>
                </Button>
              </div>
            </div>

            {/* Video Player */}
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <video
                ref={videoRef}
                className="w-full h-auto rounded-lg"
                src="/sam_promo.mov"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                controls
              />
              {!isPlaying && (
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform">
                    <Play className="h-8 w-8 md:h-10 md:w-10" />
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Section Preview */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">
                {t("about.title")}
              </h2>
              <h3 className="text-xl text-primary mb-6">
                {t("about.subtitle")}
              </h3>
              <p className="mb-6">{t("about.bio")}</p>
              <Button asChild>
                <NavLink to="/about">
                  {t("hero.cta")} <ChevronRight className="ml-2 h-4 w-4" />
                </NavLink>
              </Button>
            </div>
            <div
              className="rounded-lg overflow-hidden cursor-pointer"
              onClick={() => openLightbox(0)}
            >
              <img
                src="/sam.webp"
                alt="Sam Childers"
                className="w-full h-auto rounded-lg shadow-lg hover:opacity-90 transition-opacity"
              />
            </div>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="section-padding bg-muted">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div
              className="order-2 md:order-1 cursor-pointer"
              onClick={() => openLightbox(1)}
            >
              <img
                src="/images/sam-childers.webp"
                alt={t("about.photo.bike")}
                className="w-full h-auto rounded-lg shadow-lg hover:opacity-90 transition-opacity"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("about.history.title")}
              </h2>
              <div className="space-y-4">
                <p>{t("about.history.early")}</p>
                <p>{t("about.history.turning")}</p>
              </div>
              <div className="mt-8">
                <Button asChild>
                  <NavLink to="/about">
                    {t("hero.cta")} <ChevronRight className="ml-2 h-4 w-4" />
                  </NavLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section Preview */}
      <section className="section-padding bg-secondary">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">
              {t("events.title")}
            </h2>
            <p className="text-xl text-primary mb-6">{t("events.subtitle")}</p>
            <p className="max-w-2xl mx-auto">{t("events.description")}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Event Card 1 - 7 maja 10:00 */}
            <div className="bg-card rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center text-primary mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">7 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">ZK Wronki</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Wronki</span>
                </div>
                <div className="flex items-center text-muted-foreground mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>10:00</span>
                </div>
              </div>
            </div>

            {/* Event Card 2 - 7 maja 18:00 */}
            <div className="bg-card rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center text-primary mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">7 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">KZ Swarzędz</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Swarzędz</span>
                </div>
                <div className="flex items-center text-muted-foreground mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>18:00</span>
                </div>
                <p className="text-sm text-muted-foreground">Spotkanie otwarte</p>
              </div>
            </div>

            {/* Event Card 3 - 8 maja */}
            <div className="bg-card rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center text-primary mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">8 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Kościół Uliczny</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Poznań</span>
                </div>
                <div className="flex items-center text-muted-foreground mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>19:00</span>
                </div>
              </div>
            </div>

            {/* Event Card 4 - 9 maja 14:00 */}
            <div className="bg-card rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center text-primary mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">9 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Baza wojsk USA</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Poznań</span>
                </div>
                <div className="flex items-center text-muted-foreground mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>14:00</span>
                </div>
                <p className="text-sm text-muted-foreground">Szczegóły wkrótce</p>
              </div>
            </div>

            {/* Business Event Card - 9 maja 17:00 */}
            <div className="bg-card rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center text-primary mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">9 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Sam Childers dla liderów biznesu</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Hotel Mercure</span>
                </div>
                <div className="flex items-center text-muted-foreground mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>17:00</span>
                </div>
                <Button variant="secondary" className="w-full" asChild>
                  <a href="https://biznes.kaznodziejazkarabinem.pl" target="_blank" rel="noopener noreferrer">
                    {t("final.register")}
                  </a>
                </Button>
              </div>
            </div>

            {/* Featured Event Card - 10 maja */}
            <div className="bg-primary text-primary-foreground rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <Calendar className="mr-2 h-5 w-5" />
                  <span className="font-medium">10 maja 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{t("final.title")}</h3>
                <div className="flex items-center opacity-90 mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Stadion Miejski, Poznań</span>
                </div>
                <div className="flex items-center opacity-90 mb-4">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>17:00</span>
                </div>
                <Button variant="secondary" className="w-full" asChild>
                  <NavLink to="/final">{t("final.register")}</NavLink>
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <NavLink to="/events">
                {t("events.view.all")}
                <ChevronRight className="ml-2 h-4 w-4" />
              </NavLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">
              {t("nav.gallery")}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.slice(2).map((image, index) => (
              <img
                key={index}
                src={image.src}
                alt={image.alt}
                className="rounded-lg w-full h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openLightbox(index + 2)}
              />
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <NavLink to="/gallery">
                {t("gallery.view.more")}{" "}
                <ChevronRight className="ml-2 h-4 w-4" />
              </NavLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-black text-primary-foreground">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("final.date")}
              </h2>
              <p className="text-xl mb-8">{t("final.join.description")}</p>
              <Button variant="secondary" size="lg" asChild>
                <NavLink to="/final">{t("final.join.title")}</NavLink>
              </Button>
            </div>
            <div className="hidden md:block">
              <img
                src="/stadion-miejski.jpg"
                alt={t("final.stadium.image.alt")}
                className="rounded-lg w-full h-auto shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={currentImageIndex}
        slides={images.map((img) => ({ src: img.src }))}
      />
    </>
  );
};

export default Index;
